/**
 * "A new version is ready" with Restart to update (M57). Shown once a newer build is deployed; the
 * app never restarts on its own. When something here isn't saved yet or is still running (an edit,
 * an upload, a send), restarting asks first and says it would be lost.
 */
import { useEffect, useState } from 'react';
import { CHECK_EVERY_MS, newerBuildDeployed } from '../lib/updates.ts';
import { hasAnyUnsaved, stopAskingBeforeLeaving } from '../lib/unsaved.ts';
import { useI18n } from '../lib/i18n.tsx';
import { Banner, Button } from '../ui/ui.tsx';

export function UpdateBanner({ restart = () => location.reload() }: { restart?: () => void }) {
  const { t } = useI18n();
  const [ready, setReady] = useState(false);
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    if (ready) return;
    let stopped = false;
    const check = () => { /* ignored: newerBuildDeployed never rejects */ if (!document.hidden) void newerBuildDeployed().then(newer => { if (newer && !stopped) setReady(true); }); };
    check();
    const timer = setInterval(check, CHECK_EVERY_MS);
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVisible);
    addEventListener('focus', onVisible);
    return () => { stopped = true; clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); removeEventListener('focus', onVisible); };
  }, [ready]);
  if (!ready) return null;
  if (confirming) return (
    <div onKeyDown={e => { if (e.key === 'Escape') setConfirming(false); }}>
      <Banner tone="attention" action={<span className="banner-actions">
        <Button autoFocus variant="danger" onClick={() => { stopAskingBeforeLeaving(); restart(); }}>{t('update.restartAnyway')}</Button>
        <Button onClick={() => setConfirming(false)}>{t('update.notNow')}</Button></span>}>{t('update.inProgress')}</Banner>
    </div>
  );
  return <Banner tone="info" action={<Button onClick={() => { if (hasAnyUnsaved()) setConfirming(true); else restart(); }}>{t('update.restart')}</Button>}>{t('update.ready')}</Banner>;
}
